import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Modal, OverlayTrigger, Row, Spinner, Table, Tooltip } from 'react-bootstrap';
import { useAuth } from 'react-oidc-context';
import { useAdmin } from '../../contexts/AdminContext';
import RequireAdmin from '../../components/admin/RequireAdmin';
import {
  BankBenchmarkTestMeta,
  BenchmarkRunSummary,
  BenchmarkTestMeta,
  TestResult,
  fetchBankBenchmarkRuns,
  fetchBankBenchmarkTests,
  fetchBenchmarkRuns,
  fetchBenchmarkTests,
  triggerBankBenchmarkRun,
  triggerBenchmarkRun,
} from '../../api/benchmarks';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function f1Color(f1: number | null | undefined): string {
  if (f1 == null) return 'secondary';
  if (f1 >= 0.9) return 'success';
  if (f1 >= 0.75) return 'warning';
  return 'danger';
}

function fmtSeconds(s?: number) {
  if (s == null) return '—';
  return s >= 60 ? `${(s / 60).toFixed(1)}m` : `${s.toFixed(0)}s`;
}

function fmtRunAt(iso?: string) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// ---------------------------------------------------------------------------
// Layer funnel bar
// ---------------------------------------------------------------------------
interface LayerBarProps {
  label: string;
  found: number;
  total: number;
  highlight?: boolean;
  color?: string;
}
const LayerBar: React.FC<LayerBarProps> = ({ label, found, total, highlight, color = 'primary' }) => {
  const ratio = total > 0 ? found / total : 0;
  const pctStr = pct(ratio);
  return (
    <div className={`mb-2 p-2 rounded ${highlight ? 'border border-primary' : ''}`} style={{ background: highlight ? '#f0f5ff' : undefined }}>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="small fw-semibold">{label}{highlight && <Badge bg="primary" className="ms-2" style={{ fontSize: '0.6rem' }}>headline</Badge>}</span>
        <span className="small text-muted">{found}/{total} ({pctStr})</span>
      </div>
      <div className="progress" style={{ height: 10 }}>
        <div
          className={`progress-bar bg-${color}`}
          style={{ width: pctStr }}
          role="progressbar"
          aria-valuenow={found}
          aria-valuemin={0}
          aria-valuemax={total}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Coverage row
// ---------------------------------------------------------------------------
const CoverageRow: React.FC<{ field: string; value: number }> = ({ field, value }) => (
  <tr>
    <td className="text-muted" style={{ fontSize: '0.8rem' }}>{field}</td>
    <td style={{ width: 180 }}>
      <div className="d-flex align-items-center gap-2">
        <div className="progress flex-grow-1" style={{ height: 7 }}>
          <div
            className={`progress-bar bg-${value >= 0.8 ? 'success' : value >= 0.5 ? 'warning' : 'danger'}`}
            style={{ width: pct(value) }}
          />
        </div>
        <span style={{ fontSize: '0.75rem', minWidth: 36 }}>{pct(value)}</span>
      </div>
    </td>
  </tr>
);

// ---------------------------------------------------------------------------
// Single test result detail panel
// ---------------------------------------------------------------------------
const TestDetail: React.FC<{ result: TestResult }> = ({ result }) => {
  const m = result.metrics;
  if (!m) {
    return (
      <Alert variant="danger" className="mt-2 mb-0 py-2 small">
        {result.status === 'error' ? `Error: ${result.error}` : 'No metrics available'}
      </Alert>
    );
  }
  const golden = m.golden_count;
  const tp = m.true_positives;
  const fp = m.false_positives;
  const fn = m.false_negatives;
  return (
    <div className="mt-2 p-3 rounded" style={{ background: '#f8f9fa' }}>
      <Row className="g-3">
        {/* Accuracy summary */}
        <Col xs={12} md={6}>
          <div className="fw-semibold small mb-2">Accuracy</div>
          <LayerBar label="Matched entries (TP)" found={tp} total={golden} color="primary" highlight />
          <div className="d-flex gap-3 mt-2" style={{ fontSize: '0.8rem' }}>
            <span className="text-success">✓ {tp} matched</span>
            <span className="text-danger">✗ {fn} missed</span>
            <span className="text-warning">+ {fp} extra</span>
          </div>
          <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
            Golden: {golden} entries · Extracted: {m.extracted_count}
          </div>
          {result.source_file && (
            <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>Source: {result.source_file}</div>
          )}
        </Col>
        {/* Optional field coverage */}
        <Col xs={12} md={6}>
          {Object.keys(m.optional_field_coverage || {}).length > 0 && (
            <>
              <div className="fw-semibold small mb-2">Optional field coverage</div>
              <table className="w-100"><tbody>
                {Object.entries(m.optional_field_coverage!).map(([field, val]) => (
                  <CoverageRow key={field} field={field} value={val} />
                ))}
              </tbody></table>
            </>
          )}
        </Col>
      </Row>

      {/* Sample unmatched — full width so table can scroll horizontally */}
      {(m.sample_unmatched_golden || []).length > 0 && (
        <div className="mt-3">
          <div className="fw-semibold small mb-1">
            Sample unmatched (golden)
            <span className="text-muted fw-normal ms-2" style={{ fontSize: '0.7rem' }}>
              — entries present in golden that were NOT extracted (false negatives)
            </span>
          </div>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 220, fontSize: '0.75rem', border: '1px solid #dee2e6', borderRadius: 4 }}>
            <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                <tr>
                  <th className="text-muted fw-normal px-2 py-1" style={{ whiteSpace: 'nowrap', borderBottom: '1px solid #dee2e6' }}>Date</th>
                  <th className="text-muted fw-normal px-2 py-1" style={{ whiteSpace: 'nowrap', borderBottom: '1px solid #dee2e6' }}>Account</th>
                  <th className="text-muted fw-normal px-2 py-1 text-end" style={{ whiteSpace: 'nowrap', borderBottom: '1px solid #dee2e6' }}>Debit</th>
                  <th className="text-muted fw-normal px-2 py-1 text-end" style={{ whiteSpace: 'nowrap', borderBottom: '1px solid #dee2e6' }}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {m.sample_unmatched_golden!.map((e, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td className="px-2 py-1" style={{ whiteSpace: 'nowrap' }}>{e.entry_date}</td>
                    <td className="px-2 py-1" style={{ whiteSpace: 'nowrap' }}>{e.account_name}</td>
                    <td className="px-2 py-1 text-end" style={{ whiteSpace: 'nowrap', color: parseFloat(e.debit) > 0 ? '#c0392b' : undefined }}>
                      {parseFloat(e.debit) > 0 ? `$${parseFloat(e.debit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                    </td>
                    <td className="px-2 py-1 text-end" style={{ whiteSpace: 'nowrap', color: parseFloat(e.credit) > 0 ? '#27ae60' : undefined }}>
                      {parseFloat(e.credit) > 0 ? `$${parseFloat(e.credit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Run detail panel
// ---------------------------------------------------------------------------
const RunDetail: React.FC<{ run: BenchmarkRunSummary }> = ({ run }) => {
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div>
      {/* Headline metrics */}
      <Row className="g-3 mb-4">
        <Col xs={6} sm={3}>
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body className="py-2">
              <div className={`fs-2 fw-bold text-${f1Color(run.overall_f1)}`}>
                {run.overall_f1 != null ? pct(run.overall_f1) : '—'}
              </div>
              <div className="text-muted small">Overall F1</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body className="py-2">
              <div className={`fs-2 fw-bold text-${f1Color(run.overall_recall)}`}>
                {run.overall_recall != null ? pct(run.overall_recall) : '—'}
              </div>
              <div className="text-muted small">Recall</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body className="py-2">
              <div className={`fs-2 fw-bold ${run.tests_failed > 0 ? 'text-danger' : 'text-success'}`}>
                {run.tests_passed}/{run.tests_run}
              </div>
              <div className="text-muted small">Tests passed</div>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} sm={3}>
          <Card className="text-center border-0 shadow-sm h-100">
            <Card.Body className="py-2">
              <div className="fs-2 fw-bold text-secondary">{fmtSeconds(run.total_extraction_seconds)}</div>
              <div className="text-muted small">Total time</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Per-test table */}
      <div className="fw-semibold mb-2">Test results</div>
      <Table size="sm" hover className="mb-0">
        <thead className="table-light">
          <tr>
            <th>Test</th>
            <th className="text-center">F1</th>
            <th className="text-center">Precision</th>
            <th className="text-center">Recall</th>
            <th className="text-center">Entries</th>
            <th className="text-center">Time</th>
            <th className="text-center">Status</th>
          </tr>
        </thead>
        <tbody>
          {run.results.map(r => (
            <React.Fragment key={r.test_name}>
              <tr
                onClick={() => setExpanded(prev => prev === r.test_name ? null : r.test_name)}
                style={{ cursor: 'pointer' }}
                className={expanded === r.test_name ? 'table-active' : ''}
              >
                <td>
                  <span className="me-1" style={{ fontSize: '0.75rem' }}>{expanded === r.test_name ? '▾' : '▸'}</span>
                  <span className="fw-semibold" style={{ fontSize: '0.85rem' }}>{r.test_name}</span>
                </td>
                <td className="text-center">
                  <Badge bg={f1Color(r.f1 ?? r.metrics?.f1)}>
                    {r.f1 != null ? pct(r.f1) : r.metrics?.f1 != null ? pct(r.metrics.f1) : '—'}
                  </Badge>
                </td>
                <td className="text-center text-muted" style={{ fontSize: '0.85rem' }}>
                  {r.precision != null ? pct(r.precision) : r.metrics?.precision != null ? pct(r.metrics.precision) : '—'}
                </td>
                <td className="text-center text-muted" style={{ fontSize: '0.85rem' }}>
                  {r.recall != null ? pct(r.recall) : r.metrics?.recall != null ? pct(r.metrics.recall) : '—'}
                </td>
                <td className="text-center text-muted" style={{ fontSize: '0.85rem' }}>
                  {r.golden_entry_count ?? r.golden ?? '—'} golden
                  {r.extracted_entry_count != null && ` / ${r.extracted_entry_count} ext.`}
                </td>
                <td className="text-center text-muted" style={{ fontSize: '0.85rem' }}>
                  {fmtSeconds(r.extraction_seconds ?? r.seconds)}
                </td>
                <td className="text-center">
                  <Badge bg={r.status === 'success' ? 'success' : r.status === 'dry_run' ? 'secondary' : 'danger'}>
                    {r.status}
                  </Badge>
                </td>
              </tr>
              {expanded === r.test_name && (
                <tr>
                  <td colSpan={7} className="p-0">
                    <div className="px-3 pb-3">
                      <TestDetail result={r} />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Trigger run modal (generic — works for GL tests and bank tests)
// ---------------------------------------------------------------------------
const TriggerModal: React.FC<{
  show: boolean;
  onHide: () => void;
  title: string;
  tests: Array<{ name: string; subtitle?: string }>;
  onTrigger: (testNames: string[], dryRun: boolean) => Promise<void>;
}> = ({ show, onHide, title, tests, onTrigger }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [dryRun, setDryRun] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (name: string) =>
    setSelected(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const handleSubmit = async () => {
    setRunning(true);
    setError(null);
    try {
      await onTrigger(selected, dryRun);
      onHide();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to trigger run');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        <div className="mb-3">
          <div className="fw-semibold small mb-2">
            Tests to run &nbsp;
            <span className="text-muted fw-normal">(leave all unchecked to run all)</span>
          </div>
          {tests.length === 0 && (
            <div className="text-muted small">No golden tests found in S3.</div>
          )}
          {tests.map(t => (
            <div key={t.name} className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id={`bench-${t.name}`}
                checked={selected.includes(t.name)}
                onChange={() => toggle(t.name)}
              />
              <label className="form-check-label small" htmlFor={`bench-${t.name}`}>
                <span className="fw-semibold">{t.name}</span>
                {t.subtitle && (
                  <span className="text-muted"> — {t.subtitle}</span>
                )}
              </label>
            </div>
          ))}
        </div>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="bench-dry-run"
            checked={dryRun}
            onChange={e => setDryRun(e.target.checked)}
          />
          <label className="form-check-label small" htmlFor="bench-dry-run">
            Dry run (verify golden files load without invoking extraction)
          </label>
        </div>
        <Alert variant="info" className="mt-3 py-2 small mb-0">
          Benchmark runs can take <strong>5–15 minutes</strong> depending on the number of test files.
          The run triggers asynchronously — refresh the page after a few minutes to see results.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={running}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={running}>
          {running && <Spinner size="sm" className="me-2" />}
          {running ? 'Triggering…' : 'Run'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Benchmarks() {
  const auth = useAuth();
  const { isSuperAdmin } = useAdmin();

  type TabType = 'gl' | 'bank';
  const [activeTab, setActiveTab] = useState<TabType>('gl');

  // GL state
  const [glRuns, setGlRuns] = useState<BenchmarkRunSummary[]>([]);
  const [glTests, setGlTests] = useState<BenchmarkTestMeta[]>([]);
  const [glSelectedRun, setGlSelectedRun] = useState<BenchmarkRunSummary | null>(null);

  // Bank state
  const [bankRuns, setBankRuns] = useState<BenchmarkRunSummary[]>([]);
  const [bankTests, setBankTests] = useState<BankBenchmarkTestMeta[]>([]);
  const [bankSelectedRun, setBankSelectedRun] = useState<BenchmarkRunSummary | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTrigger, setShowTrigger] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const token = auth.user?.id_token;

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      if (activeTab === 'gl') {
        const [runsData, testsData] = await Promise.all([
          fetchBenchmarkRuns(token),
          fetchBenchmarkTests(token),
        ]);
        setGlRuns(runsData.runs);
        setGlTests(testsData.tests);
        if (runsData.runs.length > 0 && !glSelectedRun) {
          setGlSelectedRun(runsData.runs[0]);
        }
      } else {
        const [runsData, testsData] = await Promise.all([
          fetchBankBenchmarkRuns(token),
          fetchBankBenchmarkTests(token),
        ]);
        setBankRuns(runsData.runs);
        setBankTests(testsData.tests);
        if (runsData.runs.length > 0 && !bankSelectedRun) {
          setBankSelectedRun(runsData.runs[0]);
        }
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load benchmark data');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTrigger = async (testNames: string[], dryRun: boolean) => {
    let result;
    if (activeTab === 'gl') {
      result = await triggerBenchmarkRun(
        { test_names: testNames.length > 0 ? testNames : undefined, dry_run: dryRun || undefined },
        token
      );
    } else {
      result = await triggerBankBenchmarkRun(
        { test_names: testNames.length > 0 ? testNames : undefined, dry_run: dryRun || undefined },
        token
      );
    }
    setSuccessMsg(result.message);
    setTimeout(() => setSuccessMsg(null), 8000);
  };

  // Derive active tab data
  const runs = activeTab === 'gl' ? glRuns : bankRuns;
  const selectedRun = activeTab === 'gl' ? glSelectedRun : bankSelectedRun;
  const setSelectedRun = activeTab === 'gl' ? setGlSelectedRun : setBankSelectedRun;
  const glTestsForModal: Array<{ name: string; subtitle?: string }> = glTests.map(t => ({
    name: t.name,
    subtitle: t.entry_count ? `${t.entry_count} entries, ${t.account_count} accounts` : undefined,
  }));
  const bankTestsForModal: Array<{ name: string; subtitle?: string }> = bankTests.map(t => ({
    name: t.name,
    subtitle: t.transaction_count ? `${t.transaction_count} txns, ${t.statement_count} statements` : undefined,
  }));
  const modalTests = activeTab === 'gl' ? glTestsForModal : bankTestsForModal;
  const modalTitle = activeTab === 'gl' ? 'Run GL Benchmarks' : 'Run Bank Statement Benchmarks';

  if (!isSuperAdmin) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">Super admin access required.</Alert>
      </Container>
    );
  }

  return (
    <RequireAdmin requireSuperAdmin>
      <Container fluid className="py-3">
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <div>
            <h4 className="mb-0">
              <i className="bi bi-speedometer2 me-2" />
              Benchmarks
            </h4>
            <div className="text-muted small">Pipeline accuracy testing against curated golden datasets</div>
            {/* Tab switcher */}
            <div className="d-flex gap-2 mt-2">
              <button
                className={`btn btn-sm ${activeTab === 'gl' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setActiveTab('gl')}
              >
                GL Extraction
              </button>
              <button
                className={`btn btn-sm ${activeTab === 'bank' ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setActiveTab('bank')}
              >
                Bank Statement
              </button>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" size="sm" onClick={loadData} disabled={loading}>
              {loading ? <Spinner size="sm" /> : <><i className="bi bi-arrow-clockwise me-1" />Refresh</>}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setShowTrigger(true)}>
              <i className="bi bi-play-fill me-1" />
              Run Benchmarks
            </Button>
          </div>
        </div>

        {successMsg && <Alert variant="success" className="py-2 mb-3">{successMsg}</Alert>}
        {error && <Alert variant="danger" className="py-2 mb-3">{error} <Button variant="link" size="sm" className="p-0" onClick={loadData}>Retry</Button></Alert>}

        {loading && (
          <div className="text-center py-5">
            <Spinner />
            <div className="text-muted mt-2">Loading benchmark data…</div>
          </div>
        )}

        {!loading && (
          <Row className="g-0">
            {/* Left sidebar — run history */}
            <Col xs={12} md={3} className="pe-md-3 mb-4 mb-md-0">
              <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                Run History ({runs.length})
              </div>

              {runs.length === 0 && (
                <div className="text-muted small border rounded p-3 text-center">
                  No benchmark runs yet.
                  <br />
                  <Button variant="link" size="sm" className="p-0 mt-1" onClick={() => setShowTrigger(true)}>
                    Run your first benchmark →
                  </Button>
                </div>
              )}

              <div className="d-flex flex-column gap-2">
                {runs.map((run, idx) => (
                  <div
                    key={run._s3_key ?? run.run_at}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedRun(run)}
                    onKeyDown={e => e.key === 'Enter' && setSelectedRun(run)}
                    className={`rounded border p-2 ${selectedRun === run ? 'border-primary bg-primary bg-opacity-10' : 'border-light-subtle'}`}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="small fw-semibold">{fmtRunAt(run.run_at)}</span>
                      {idx === 0 && <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>latest</Badge>}
                    </div>
                    <div className="d-flex gap-2 mt-1 align-items-center">
                      <Badge bg={f1Color(run.overall_f1)} style={{ fontSize: '0.7rem' }}>
                        F1 {run.overall_f1 != null ? pct(run.overall_f1) : '—'}
                      </Badge>
                      <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {run.tests_passed}/{run.tests_run} passed
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Golden test inventory — GL */}
              {activeTab === 'gl' && glTests.length > 0 && (
                <div className="mt-4">
                  <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                    Golden Tests ({glTests.length})
                  </div>
                  {glTests.map(t => (
                    <OverlayTrigger
                      key={t.name}
                      placement="right"
                      overlay={
                        <Tooltip id={`tt-${t.name}`}>
                          {t.entry_count} entries · {t.account_count} accounts
                          {t.captured_at && <><br />Captured {new Date(t.captured_at).toLocaleDateString()}</>}
                        </Tooltip>
                      }
                    >
                      <div className="small py-1 border-bottom" style={{ fontSize: '0.8rem' }}>
                        <i className="bi bi-file-earmark-text me-1 text-muted" />
                        {t.name}
                        <span className="text-muted float-end">{t.entry_count}</span>
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
              )}
              {/* Golden test inventory — Bank */}
              {activeTab === 'bank' && bankTests.length > 0 && (
                <div className="mt-4">
                  <div className="fw-semibold small text-muted text-uppercase mb-2" style={{ letterSpacing: '0.05em' }}>
                    Golden Tests ({bankTests.length})
                  </div>
                  {bankTests.map(t => (
                    <OverlayTrigger
                      key={t.name}
                      placement="right"
                      overlay={
                        <Tooltip id={`tt-${t.name}`}>
                          {t.transaction_count} transactions · {t.statement_count} statements
                          {t.bank_name && <><br />{t.bank_name}</>}
                          {t.account_owner && <><br />{t.account_owner}</>}
                        </Tooltip>
                      }
                    >
                      <div className="small py-1 border-bottom" style={{ fontSize: '0.8rem' }}>
                        <i className="bi bi-bank me-1 text-muted" />
                        {t.name}
                        <span className="text-muted float-end">{t.transaction_count} txns</span>
                      </div>
                    </OverlayTrigger>
                  ))}
                </div>
              )}
            </Col>

            {/* Main panel — selected run */}
            <Col xs={12} md={9}>
              {selectedRun ? (
                <div>
                  <div className="text-muted small mb-3">
                    <i className="bi bi-calendar3 me-1" />
                    {new Date(selectedRun.run_at).toLocaleString()}
                    {selectedRun.run_prefix && (
                      <span className="ms-3 font-monospace" style={{ fontSize: '0.72rem' }}>
                        {selectedRun.run_prefix}
                      </span>
                    )}
                  </div>
                  <RunDetail run={selectedRun} />
                </div>
              ) : (
                <div className="text-center text-muted py-5">
                  Select a run from the history to view details
                </div>
              )}
            </Col>
          </Row>
        )}

        <TriggerModal
          show={showTrigger}
          onHide={() => setShowTrigger(false)}
          title={modalTitle}
          tests={modalTests}
          onTrigger={handleTrigger}
        />
      </Container>
    </RequireAdmin>
  );
}
